import * as THREE from 'three'
import { logger } from '@/app/logger'

// Add physically‑plausible lights for the scene
export function addLights(scene: THREE.Scene): void {
  const ambientLight = new THREE.AmbientLight(0x3b4e6b, 0.4)
  scene.add(ambientLight)

  const directionalLight = new THREE.DirectionalLight(0xeff6ff, 1.2)
  directionalLight.position.set(5, 10, 7.5)
  directionalLight.castShadow = true
  directionalLight.shadow.mapSize.width = 2048
  directionalLight.shadow.mapSize.height = 2048
  scene.add(directionalLight)

  const secondaryLight = new THREE.DirectionalLight(0x60a5fa, 0.3)
  secondaryLight.position.set(-5, 5, -5)
  scene.add(secondaryLight)

  const pointLight = new THREE.PointLight(0x3b82f6, 0.3, 50)
  pointLight.position.set(0, 8, 0)
  scene.add(pointLight)
}

// Apply background, fog, and a simple environment map
export function applyEnvironment(scene: THREE.Scene): void {
  scene.background = new THREE.Color(0x000010)
  scene.fog = new THREE.Fog(0x000010, 50, 200)
  scene.environment = createGradientEnvMap()
  addStarfield(scene)
}

// Create a small canvas‑based gradient env map
function createGradientEnvMap(): THREE.Texture {
  const canvas = document.createElement('canvas')
  canvas.width = 64
  canvas.height = 64
  const context = canvas.getContext('2d')!
  const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32)
  gradient.addColorStop(0, '#1a1a2e')
  gradient.addColorStop(0.5, '#16213e')
  gradient.addColorStop(1, '#0f172a')
  context.fillStyle = gradient
  context.fillRect(0, 0, 64, 64)
  const envTexture = new THREE.CanvasTexture(canvas)
  envTexture.mapping = THREE.EquirectangularReflectionMapping
  envTexture.colorSpace = THREE.SRGBColorSpace
  return envTexture
}

// Starfield particles around the scene
export function addStarfield(scene: THREE.Scene, starCount = 400): void {
  const stars = new THREE.BufferGeometry()
  const positions = new Float32Array(starCount * 3)
  const colors = new Float32Array(starCount * 3)
  const sizes = new Float32Array(starCount)

  for (let i = 0; i < starCount; i++) {
    const radius = 70 + Math.random() * 80
    const phi = Math.random() * Math.PI * 2
    const theta = Math.random() * Math.PI

    positions[i * 3] = radius * Math.sin(theta) * Math.cos(phi)
    positions[i * 3 + 1] = radius * Math.sin(theta) * Math.sin(phi)
    positions[i * 3 + 2] = radius * Math.cos(theta)

    const t = Math.random()
    if (t < 0.7) {
      colors[i * 3] = 0.95
      colors[i * 3 + 1] = 0.95
      colors[i * 3 + 2] = 1.0
      sizes[i] = 1.0 + Math.random() * 1.5
    } else if (t < 0.9) {
      colors[i * 3] = 1.0
      colors[i * 3 + 1] = 0.85
      colors[i * 3 + 2] = 0.7
      sizes[i] = 1.2 + Math.random() * 2.0
    } else {
      colors[i * 3] = 0.9
      colors[i * 3 + 1] = 0.9
      colors[i * 3 + 2] = 1.0
      sizes[i] = 2.0 + Math.random() * 3.0
    }
  }

  stars.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  stars.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  stars.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

  const starMaterial = new THREE.PointsMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.95,
    sizeAttenuation: false,
    blending: THREE.AdditiveBlending,
  })

  const starSystem = new THREE.Points(stars, starMaterial)
  scene.add(starSystem)
  logger.log('Starfield created:', starCount)
}
